/**
 * Hala CRM - Groups Management Page
 * Manage operational groups with members and linked services
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, Plus, Search, Edit2, Trash2, ChevronDown, ChevronUp,
  Layers, UserCheck, Settings, RefreshCw, Link2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const ASSIGNMENT_MODES = {
  manual: { label: 'يدوي', description: 'التوزيع يدوياً بواسطة المشرف' },
  round_robin: { label: 'دوري', description: 'توزيع تلقائي بالتناوب' },
  load_balanced: { label: 'متوازن', description: 'توزيع حسب عبء العمل' },
};

function GroupModal({ group, programs, services, users, onSave, onClose }) {
  const [formData, setFormData] = useState({
    group_name_ar: group?.group_name_ar || '',
    group_name_en: group?.group_name_en || '',
    description: group?.description || '',
    program_name_ar: group?.program_name_ar || '',
    linked_services: group?.linked_services || [],
    supervisors: group?.supervisors || [],
    members: group?.members || [],
    assignment_mode: group?.assignment_mode || 'manual',
    status: group?.status || 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [programServices, setProgramServices] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');

  // Filter services when program changes
  useEffect(() => {
    if (formData.program_name_ar) {
      const filtered = services.filter(s => s.program_name_ar === formData.program_name_ar);
      setProgramServices(filtered);
    } else {
      setProgramServices([]);
    }
  }, [formData.program_name_ar, services]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      linked_services: prev.linked_services.includes(serviceId)
        ? prev.linked_services.filter(s => s !== serviceId)
        : [...prev.linked_services, serviceId]
    }));
  };

  const toggleUser = (userId, type) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].includes(userId)
        ? prev[type].filter(u => u !== userId)
        : [...prev[type], userId]
    }));
  };

  // Filter users for supervisors (only supervisor/admin roles)
  const supervisorCandidates = users.filter(u => 
    ['admin', 'supervisor'].includes(u.role_id) && u.status === 'active'
  );
  
  // Filter users for members (consultant/group_user roles)
  const memberCandidates = users.filter(u => 
    ['consultant', 'group_user'].includes(u.role_id) && u.status === 'active'
  );

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
            {group ? 'تعديل المجموعة' : 'إضافة مجموعة جديدة'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}` }}>
          {[
            { id: 'basic', label: 'المعلومات الأساسية' },
            { id: 'services', label: 'الخدمات المرتبطة' },
            { id: 'members', label: 'الأعضاء والمشرفون' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? COLORS.white : COLORS.bg,
                borderBottom: activeTab === tab.id ? `2px solid ${COLORS.primary}` : 'none',
                color: activeTab === tab.id ? COLORS.primary : COLORS.textMuted,
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: COLORS.errorLight,
              color: COLORS.error,
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
            }}>
              {error}
            </div>
          )}

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                    اسم المجموعة بالعربي *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.group_name_ar}
                    onChange={(e) => setFormData({ ...formData, group_name_ar: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                    اسم المجموعة بالإنجليزي
                  </label>
                  <input
                    type="text"
                    value={formData.group_name_en}
                    onChange={(e) => setFormData({ ...formData, group_name_en: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                  البرنامج *
                </label>
                <select
                  required
                  value={formData.program_name_ar}
                  onChange={(e) => setFormData({ ...formData, program_name_ar: e.target.value, linked_services: [] })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: COLORS.white,
                  }}
                >
                  <option value="">اختر البرنامج</option>
                  {programs.map(prog => (
                    <option key={prog.program_name_ar} value={prog.program_name_ar}>
                      {prog.program_name_ar}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                  وصف المجموعة
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                    طريقة التوزيع
                  </label>
                  <select
                    value={formData.assignment_mode}
                    onChange={(e) => setFormData({ ...formData, assignment_mode: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: COLORS.white,
                    }}
                  >
                    {Object.entries(ASSIGNMENT_MODES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
                    {ASSIGNMENT_MODES[formData.assignment_mode]?.description}
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                    الحالة
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: COLORS.white,
                    }}
                  >
                    <option value="active">نشط</option>
                    <option value="inactive">غير نشط</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div>
              {!formData.program_name_ar ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: COLORS.textMuted }}>
                  <Layers size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                  <p>اختر البرنامج أولاً من تبويب المعلومات الأساسية</p>
                </div>
              ) : programServices.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: COLORS.textMuted }}>
                  <Layers size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                  <p>لا توجد خدمات لهذا البرنامج</p>
                </div>
              ) : (
                <>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: COLORS.textMuted }}>
                    اختر الخدمات التي ستتعامل معها هذه المجموعة ({formData.linked_services.length} محددة)
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {programServices.map(service => (
                      <label
                        key={service.service_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          backgroundColor: formData.linked_services.includes(service.service_id) ? `${COLORS.primary}10` : COLORS.bg,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: `1px solid ${formData.linked_services.includes(service.service_id) ? COLORS.primary : 'transparent'}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.linked_services.includes(service.service_id)}
                          onChange={() => toggleService(service.service_id)}
                          style={{ width: '18px', height: '18px', accentColor: COLORS.primary }}
                        />
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                            {service.service_name_ar}
                          </p>
                          {service.category_ar && (
                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                              {service.category_ar}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div>
              {/* Supervisors */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserCheck size={18} color={COLORS.gold} />
                  المشرفون ({formData.supervisors.length})
                </h4>
                {supervisorCandidates.length === 0 ? (
                  <p style={{ fontSize: '13px', color: COLORS.textMuted }}>لا يوجد مشرفون متاحون</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {supervisorCandidates.map(user => (
                      <button
                        key={user.user_id}
                        type="button"
                        onClick={() => toggleUser(user.user_id, 'supervisors')}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: `1px solid ${formData.supervisors.includes(user.user_id) ? COLORS.gold : COLORS.border}`,
                          backgroundColor: formData.supervisors.includes(user.user_id) ? COLORS.gold : COLORS.white,
                          color: formData.supervisors.includes(user.user_id) ? COLORS.white : COLORS.text,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {user.name_ar}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Members */}
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Users size={18} color={COLORS.primary} />
                  الأعضاء ({formData.members.length})
                </h4>
                {memberCandidates.length === 0 ? (
                  <p style={{ fontSize: '13px', color: COLORS.textMuted }}>لا يوجد أعضاء متاحون</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {memberCandidates.map(user => (
                      <button
                        key={user.user_id}
                        type="button"
                        onClick={() => toggleUser(user.user_id, 'members')}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: `1px solid ${formData.members.includes(user.user_id) ? COLORS.primary : COLORS.border}`,
                          backgroundColor: formData.members.includes(user.user_id) ? COLORS.primary : COLORS.white,
                          color: formData.members.includes(user.user_id) ? COLORS.white : COLORS.text,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {user.name_ar}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                backgroundColor: COLORS.white,
                color: COLORS.text,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'جاري الحفظ...' : 'حفظ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GroupCard({ group, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      overflow: 'hidden',
    }}>
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '20px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${COLORS.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Layers size={24} color={COLORS.primary} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              {group.group_name_ar}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              {group.program_name_ar || 'بدون برنامج'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.gold }}>{group.supervisors_count || 0}</p>
              <p style={{ margin: 0, fontSize: '11px', color: COLORS.textMuted }}>مشرفون</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.primary }}>{group.members_count || 0}</p>
              <p style={{ margin: 0, fontSize: '11px', color: COLORS.textMuted }}>أعضاء</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.success }}>{group.services_count || 0}</p>
              <p style={{ margin: 0, fontSize: '11px', color: COLORS.textMuted }}>خدمات</p>
            </div>
          </div>
          
          <span style={{
            padding: '4px 12px',
            borderRadius: '20px',
            backgroundColor: group.status === 'active' ? COLORS.successLight : COLORS.errorLight,
            color: group.status === 'active' ? COLORS.success : COLORS.error,
            fontSize: '12px',
            fontWeight: 500,
          }}>
            {group.status === 'active' ? 'نشط' : 'غير نشط'}
          </span>

          {expanded ? <ChevronUp size={20} color={COLORS.textMuted} /> : <ChevronDown size={20} color={COLORS.textMuted} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '16px' }}>
          {group.description && (
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: COLORS.textMuted }}>
              {group.description}
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              padding: '8px 14px',
              backgroundColor: COLORS.bg,
              borderRadius: '8px',
              fontSize: '12px',
            }}>
              <span style={{ color: COLORS.textMuted }}>طريقة التوزيع: </span>
              <span style={{ fontWeight: 500, color: COLORS.text }}>
                {ASSIGNMENT_MODES[group.assignment_mode]?.label || group.assignment_mode}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(group); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                backgroundColor: COLORS.white,
                color: COLORS.text,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              <Edit2 size={14} />
              تعديل
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(group.group_id); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: COLORS.errorLight,
                color: COLORS.error,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              <Trash2 size={14} />
              حذف
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CRMGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [programFilter, setProgramFilter] = useState('');
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, programsRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/crm/groups`, { 
          headers: getAuthHeaders(),
          params: { program: programFilter || undefined }
        }),
        axios.get(`${API}/crm/programs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/stats`, { headers: getAuthHeaders() }),
      ]);
      
      setGroups(groupsRes.data || []);
      setPrograms(programsRes.data || []);
      setUsers(usersRes.data.items || []);
      setStats(statsRes.data);

      // Fetch all services from catalog
      const servicesRes = await axios.get(`${API}/catalog/services`, { headers: getAuthHeaders() });
      setServices(servicesRes.data.items || []);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  }, [programFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveGroup = async (formData) => {
    if (editingGroup) {
      await axios.put(`${API}/crm/groups/${editingGroup.group_id}`, formData, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/crm/groups`, formData, { headers: getAuthHeaders() });
    }
    fetchData();
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المجموعة؟')) return;
    
    try {
      await axios.delete(`${API}/crm/groups/${groupId}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      console.error('Failed to delete group:', e);
    }
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingGroup(null);
    setShowModal(true);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
              إدارة المجموعات
            </h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              المجموعات التشغيلية المسؤولة عن معالجة الطلبات والخدمات
            </p>
          </div>
          <button
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <Plus size={18} />
            إضافة مجموعة
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Layers size={20} color={COLORS.primary} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>إجمالي المجموعات</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.text }}>{stats.groups?.total || 0}</p>
                </div>
              </div>
            </div>
            {Object.entries(stats.groups?.by_program || {}).slice(0, 3).map(([program, count]) => (
              <div key={program} style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Layers size={20} color={COLORS.gold} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>{program}</p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.gold }}>{count}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            style={{
              padding: '10px 14px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: COLORS.white,
              minWidth: '180px',
            }}
          >
            <option value="">كل البرامج</option>
            {programs.map(prog => (
              <option key={prog.program_name_ar} value={prog.program_name_ar}>{prog.program_name_ar}</option>
            ))}
          </select>
          <button
            onClick={fetchData}
            style={{
              padding: '10px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              backgroundColor: COLORS.white,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={18} color={COLORS.textMuted} />
          </button>
        </div>
      </div>

      {/* Groups List */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : groups.length === 0 ? (
        <div style={{ 
          backgroundColor: COLORS.white, 
          borderRadius: '12px', 
          padding: '60px', 
          textAlign: 'center',
          border: `1px solid ${COLORS.border}`,
        }}>
          <Layers size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
          <p style={{ margin: 0, color: COLORS.textMuted }}>لا توجد مجموعات</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {groups.map(group => (
            <GroupCard
              key={group.group_id}
              group={group}
              onEdit={openEditModal}
              onDelete={handleDeleteGroup}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <GroupModal
          group={editingGroup}
          programs={programs}
          services={services}
          users={users}
          onSave={handleSaveGroup}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
